import * as React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Drawer,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import theme from "../../../theme";
import { useSnackbar } from "../../../components/common/SnackbarContext";
import {
  createPricingGroup,
  deleteGroupBrandMargin,
  deletePricingGroup,
  getGroupBrandMargins,
  getPricingGroups,
  updatePricingGroup,
  upsertGroupBrandMargins,
} from "../../../services/core-api";
import { fetchBrands } from "../../../services/tecdocService";
import { Brand } from "../../../interfaces/Brand";
import { GroupBrandMargin, PricingGroup } from "../../../interfaces/core/PricingGroup";

const pctToDecimal = (pct: string | number) => Number(pct) / 100;
const decimalToPct = (value: number) => (value * 100).toFixed(2);

const mergeBrandMargins = (
  current: GroupBrandMargin[],
  incoming: GroupBrandMargin[]
): GroupBrandMargin[] => {
  const bySupplierId = new Map(current.map((row) => [row.data_supplier_id, row]));
  for (const row of incoming) {
    bySupplierId.set(row.data_supplier_id, row);
  }
  return Array.from(bySupplierId.values()).sort(
    (a, b) =>
      (a.brand_name ?? "").localeCompare(b.brand_name ?? "") ||
      a.data_supplier_id - b.data_supplier_id
  );
};

const PricingGroupsPage = () => {
  const { showSnackbar } = useSnackbar();
  const [groups, setGroups] = React.useState<PricingGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = React.useState<PricingGroup | null>(null);
  const [brandMargins, setBrandMargins] = React.useState<GroupBrandMargin[]>([]);
  const [brands, setBrands] = React.useState<Brand[]>([]);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [editingGroup, setEditingGroup] = React.useState<PricingGroup | null>(null);
  const [groupForm, setGroupForm] = React.useState({
    code: "",
    name: "",
    marginPct: "",
    sortOrder: "0",
    isActive: true,
  });
  const [brandForm, setBrandForm] = React.useState({
    brand: null as Brand | null,
    marginPct: "",
  });

  const loadGroups = React.useCallback(async () => {
    try {
      const response = await getPricingGroups(false);
      setGroups(response);
    } catch {
      showSnackbar("Preisgruppen konnten nicht geladen werden.", "error");
    }
  }, [showSnackbar]);

  const loadBrandMargins = React.useCallback(async (groupId: number) => {
    try {
      const response = await getGroupBrandMargins(groupId);
      setBrandMargins(response);
    } catch {
      setBrandMargins([]);
      showSnackbar("Markenmargen konnten nicht geladen werden.", "error");
    }
  }, [showSnackbar]);

  React.useEffect(() => {
    loadGroups();
    fetchBrands().then(setBrands).catch(() => setBrands([]));
  }, [loadGroups]);

  React.useEffect(() => {
    if (groups.length > 0 && !selectedGroup) {
      setSelectedGroup(groups[0]);
    }
  }, [groups, selectedGroup]);

  React.useEffect(() => {
    if (selectedGroup) {
      loadBrandMargins(selectedGroup.id);
    } else {
      setBrandMargins([]);
    }
  }, [selectedGroup, loadBrandMargins]);

  const openCreateDrawer = () => {
    setEditingGroup(null);
    setGroupForm({ code: "", name: "", marginPct: "", sortOrder: "0", isActive: true });
    setDrawerOpen(true);
  };

  const openEditDrawer = (group: PricingGroup) => {
    setEditingGroup(group);
    setGroupForm({
      code: group.code,
      name: group.name,
      marginPct: decimalToPct(group.margin_pct),
      sortOrder: String(group.sort_order),
      isActive: group.is_active,
    });
    setDrawerOpen(true);
  };

  const handleSaveGroup = async () => {
    const payload = {
      code: groupForm.code.trim().toUpperCase(),
      name: groupForm.name.trim(),
      margin_pct: pctToDecimal(groupForm.marginPct || "0"),
      sort_order: Number(groupForm.sortOrder || 0),
      is_active: groupForm.isActive,
    };

    try {
      if (editingGroup) {
        await updatePricingGroup(editingGroup.id, payload);
        showSnackbar("Preisgruppe aktualisiert.", "success");
      } else {
        await createPricingGroup(payload);
        showSnackbar("Preisgruppe erstellt.", "success");
      }
      setDrawerOpen(false);
      await loadGroups();
    } catch {
      showSnackbar("Preisgruppe konnte nicht gespeichert werden.", "error");
    }
  };

  const handleDeleteGroup = async (group: PricingGroup) => {
    try {
      await deletePricingGroup(group.id);
      if (selectedGroup?.id === group.id) {
        setSelectedGroup(null);
      }
      showSnackbar("Preisgruppe gelöscht.", "success");
      await loadGroups();
    } catch {
      showSnackbar("Preisgruppe konnte nicht gelöscht werden.", "error");
    }
  };

  const handleAddBrandMargin = async () => {
    if (!selectedGroup) {
      showSnackbar("Bitte zuerst eine Preisgruppe links auswählen.", "error");
      return;
    }
    if (!brandForm.brand) {
      showSnackbar("Bitte eine Marke auswählen.", "error");
      return;
    }
    if (!brandForm.marginPct.trim()) {
      showSnackbar("Bitte Extra-Marge in Prozent eingeben.", "error");
      return;
    }

    try {
      const savedRows = await upsertGroupBrandMargins(selectedGroup.id, [{
        data_supplier_id: brandForm.brand.dataSupplierId,
        brand_name: brandForm.brand.mfrName,
        margin_pct: pctToDecimal(brandForm.marginPct || "0"),
      }]);
      setBrandForm({ brand: null, marginPct: "" });
      setBrandMargins((prev) => mergeBrandMargins(prev, savedRows));
      showSnackbar("Markenmarge gespeichert.", "success");
    } catch {
      showSnackbar("Markenmarge konnte nicht gespeichert werden.", "error");
    }
  };

  const handleDeleteBrandMargin = async (row: GroupBrandMargin) => {
    if (!selectedGroup) {
      return;
    }

    try {
      await deleteGroupBrandMargin(selectedGroup.id, row.data_supplier_id);
      setBrandMargins((prev) => prev.filter((item) => item.data_supplier_id !== row.data_supplier_id));
      showSnackbar("Markenmarge entfernt.", "success");
    } catch {
      showSnackbar("Markenmarge konnte nicht entfernt werden.", "error");
    }
  };

  return (
    <>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
        Preisgruppen & Markenmargen
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Preisgruppen
            </Typography>
            <Button startIcon={<AddIcon />} onClick={openCreateDrawer}>
              Neu
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell align="right">Marge %</TableCell>
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map((group) => (
                  <TableRow
                    key={group.id}
                    hover
                    selected={selectedGroup?.id === group.id}
                    onClick={() => setSelectedGroup(group)}
                    sx={{
                      cursor: "pointer",
                      "&.Mui-selected": {
                        backgroundColor: "rgba(25, 118, 210, 0.12)",
                      },
                      "&.Mui-selected:hover": {
                        backgroundColor: "rgba(25, 118, 210, 0.18)",
                      },
                    }}
                  >
                    <TableCell>{group.code}</TableCell>
                    <TableCell>{group.name}</TableCell>
                    <TableCell align="right">{decimalToPct(group.margin_pct)}%</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditDrawer(group); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteGroup(group); }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        <Grid item xs={12} md={7}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Markenmargen {selectedGroup ? `(${selectedGroup.code})` : ""}
          </Typography>

          {selectedGroup ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Extra-Marge für Marke in Gruppe <strong>{selectedGroup.code}</strong> — wird zusätzlich zur Gruppenmarge ({decimalToPct(selectedGroup.margin_pct)}%) angewendet.
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
                <Autocomplete
                  options={brands}
                  getOptionLabel={(option) => `${option.mfrName} (${option.dataSupplierId})`}
                  value={brandForm.brand}
                  onChange={(_, value) => setBrandForm((prev) => ({ ...prev, brand: value }))}
                  sx={{ minWidth: 280, flex: 1 }}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Marke wählen" size="small" />
                  )}
                />
                <TextField
                  placeholder="Extra-Marge %"
                  size="small"
                  value={brandForm.marginPct}
                  onChange={(e) => setBrandForm((prev) => ({ ...prev, marginPct: e.target.value }))}
                  sx={{ width: 140 }}
                />
                <Button variant="contained" onClick={handleAddBrandMargin}>
                  Hinzufügen
                </Button>
              </Box>

              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Marke</TableCell>
                      <TableCell>ID</TableCell>
                      <TableCell align="right">Extra-Marge %</TableCell>
                      <TableCell align="right">Aktion</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {brandMargins.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.brand_name || "—"}</TableCell>
                        <TableCell>{row.data_supplier_id}</TableCell>
                        <TableCell align="right">{decimalToPct(row.margin_pct)}%</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleDeleteBrandMargin(row)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Paper sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Wählen Sie links eine Preisgruppe (z. B. A, B, C), um Markenmargen zu verwalten.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tipp: Klicken Sie auf eine Zeile in der Tabelle „Preisgruppen“ — die Markenauswahl erscheint dann hier.
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 360, p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {editingGroup ? "Preisgruppe bearbeiten" : "Preisgruppe erstellen"}
          </Typography>
          <TextField
            fullWidth
            label="Code"
            margin="normal"
            value={groupForm.code}
            onChange={(e) => setGroupForm((prev) => ({ ...prev, code: e.target.value }))}
          />
          <TextField
            fullWidth
            label="Name"
            margin="normal"
            value={groupForm.name}
            onChange={(e) => setGroupForm((prev) => ({ ...prev, name: e.target.value }))}
          />
          <TextField
            fullWidth
            label="Gruppenmarge %"
            margin="normal"
            value={groupForm.marginPct}
            onChange={(e) => setGroupForm((prev) => ({ ...prev, marginPct: e.target.value }))}
          />
          <TextField
            fullWidth
            label="Sortierung"
            margin="normal"
            value={groupForm.sortOrder}
            onChange={(e) => setGroupForm((prev) => ({ ...prev, sortOrder: e.target.value }))}
          />
          <FormGroup sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={groupForm.isActive}
                  onChange={(e) => setGroupForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                />
              }
              label="Aktiv"
            />
          </FormGroup>
          <Button
            fullWidth
            sx={{ mt: 3, bgcolor: theme.palette.primary.dark, color: "#fff" }}
            onClick={handleSaveGroup}
          >
            Speichern
          </Button>
        </Box>
      </Drawer>
    </>
  );
};

export default PricingGroupsPage;
